import { FARMTOTABLE } from "@/constants/testIds";
import { Wheat, Filter, Factory, Plane, Coffee } from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: Wheat,
    title: "Nigerian Farmers",
    body:
      "Generations-old cashew farms in Imo and Abia, hand-harvested at peak ripeness by farming families who know every tree.",
  },
  {
    n: "02",
    icon: Filter,
    title: "Careful Selection",
    body:
      "Kernels are sorted by size, colour and integrity. Only the upper grades carry the NAIJA name — the rest goes back to the local market.",
  },
  {
    n: "03",
    icon: Factory,
    title: "Quality Processing",
    body:
      "Processed locally in Nigeria with modern food-safety standards. Local processing means more value stays at origin.",
  },
  {
    n: "04",
    icon: Plane,
    title: "International Distribution",
    body:
      "Cool-chain shipping from West Africa to our Berlin hub, then onward to retailers and homes across Europe.",
  },
  {
    n: "05",
    icon: Coffee,
    title: "European Consumers",
    body:
      "A premium pantry staple on tables from Berlin to Lisbon — origin pride, in every kernel.",
  },
];

export const FarmToTable = () => {
  return (
    <section
      id="journey"
      data-testid={FARMTOTABLE.wrapper}
      className="relative py-24 sm:py-32 bg-[#fdfaf4]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#c5a059]" />
            <span className="overline">The Journey</span>
          </div>
          <h2 className="font-serif-display text-[40px] sm:text-[56px] leading-[1.05] text-[#1a3626] tracking-tight">
            From Farm <em className="italic text-[#8b5a2b]">to Table.</em>
          </h2>
          <p className="mt-6 text-[16px] sm:text-[17px] leading-[1.7] text-[#5c4033] max-w-xl">
            Five careful steps from a Nigerian orchard to your kitchen — each
            one a deliberate choice for quality, transparency and origin value.
          </p>
        </div>

        {/* Desktop / tablet timeline */}
        <div className="hidden md:block relative">
          {/* Connecting line */}
          <div className="absolute top-12 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#c5a059]/45 to-transparent" />
          <ol className="grid grid-cols-5 gap-4 lg:gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <li
                  key={i}
                  data-testid={FARMTOTABLE.step(i)}
                  className="relative flex flex-col items-center text-center group"
                >
                  <div className="relative z-10 w-24 h-24 rounded-full bg-[#faf7f2] border border-[#c5a059]/30 flex items-center justify-center shadow-[0_10px_30px_rgba(26,54,38,0.08)] group-hover:-translate-y-1 transition-all duration-500">
                    <div className="w-16 h-16 rounded-full bg-[#1a3626] text-[#c5a059] flex items-center justify-center">
                      <Icon size={22} />
                    </div>
                  </div>
                  <div className="mt-5 text-[10.5px] uppercase tracking-[0.24em] text-[#8b5a2b]">
                    Step {s.n}
                  </div>
                  <h3 className="mt-2 font-serif-display text-[22px] lg:text-[24px] text-[#1a3626] tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-[13.5px] leading-[1.65] text-[#5c4033]">
                    {s.body}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Mobile vertical timeline */}
        <ol className="md:hidden relative pl-12">
          <span className="absolute left-[22px] top-2 bottom-2 w-px bg-gradient-to-b from-[#c5a059]/40 via-[#c5a059]/40 to-transparent" />
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <li
                key={i}
                data-testid={`${FARMTOTABLE.step(i)}-mobile`}
                className="relative mb-10 last:mb-0"
              >
                <div className="absolute -left-12 top-0 w-11 h-11 rounded-full bg-[#1a3626] text-[#c5a059] flex items-center justify-center shadow-[0_8px_20px_rgba(26,54,38,0.18)]">
                  <Icon size={17} />
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#8b5a2b]">
                  Step {s.n}
                </div>
                <h3 className="mt-1 font-serif-display text-[22px] text-[#1a3626] tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-2 text-[14px] leading-[1.7] text-[#5c4033]">
                  {s.body}
                </p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};
